import { PersistedWorkflow } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';
import type {
  WorkflowCronTriggerViewData,
  WorkflowDatasetTriggerViewData,
  WorkflowEventTriggerViewData,
  WorkflowTriggerViewData,
  WorkflowWebhookTriggerViewData,
} from '../types';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

export function workflowTriggerViewFromSnapshot(
  snapshot: PersistedWorkflow.PersistedWorkflowTriggerSnapshot
): WorkflowTriggerViewData {
  switch (snapshot.kind) {
    case PersistedWorkflowTriggerKind.Cron:
      return {
        kind: PersistedWorkflowTriggerKind.Cron,
        id: snapshot.trigger.id,
        name: snapshot.trigger.name,
        cron: snapshot.trigger.cron,
        timezone: snapshot.trigger.timezone,
        enabled: snapshot.trigger.enabled,
      } satisfies WorkflowCronTriggerViewData;
    case PersistedWorkflowTriggerKind.Webhook: {
      const webhookTrigger =
        snapshot.trigger as PersistedWorkflow.SerializedWorkflowWebhookTrigger & {
          hasSecret?: boolean;
        };
      return {
        kind: PersistedWorkflowTriggerKind.Webhook,
        id: webhookTrigger.id,
        name: webhookTrigger.name,
        enabled: webhookTrigger.enabled,
        hasSecret:
          webhookTrigger.hasSecret ??
          (webhookTrigger.secret !== undefined && webhookTrigger.secret !== ''),
        providerKey: webhookTrigger.providerKey,
        eventKey: webhookTrigger.eventKey,
        eventConfig: webhookTrigger.eventConfig,
        installStatus: webhookTrigger.installStatus,
      } satisfies WorkflowWebhookTriggerViewData;
    }
    case PersistedWorkflowTriggerKind.Event:
      return {
        kind: PersistedWorkflowTriggerKind.Event,
        id: snapshot.trigger.id,
        name: snapshot.trigger.name,
        eventName: snapshot.trigger.eventName,
        enabled: snapshot.trigger.enabled,
      } satisfies WorkflowEventTriggerViewData;
    case PersistedWorkflowTriggerKind.Dataset:
      return {
        kind: PersistedWorkflowTriggerKind.Dataset,
        id: snapshot.trigger.id,
        name: snapshot.trigger.name,
        datasetId: snapshot.trigger.datasetId,
        criterion: snapshot.trigger.criterion,
        trackFields: snapshot.trigger.trackFields,
        filter: snapshot.trigger.filter,
        processExistingRows: snapshot.trigger.processExistingRows,
        runMode: snapshot.trigger.runMode,
        enabled: snapshot.trigger.enabled,
      } satisfies WorkflowDatasetTriggerViewData;
    default:
      return Assert.unreachable(snapshot);
  }
}

export { cronTriggerStrategy } from './cron-trigger-strategy';
export { webhookTriggerStrategy } from './webhook-trigger-strategy';
export { eventTriggerStrategy } from './event-trigger-strategy';
export { datasetTriggerStrategy } from './dataset-trigger-strategy';
export { unknownTriggerStrategy } from './unknown-trigger-strategy';
